import axios from 'axios';
import { adminApiUrl } from '../config/api';
import { adminAuthService } from './adminAuth';

export interface PlatformStats {
  totalUsers: number;
  totalProviders: number;
  totalBookings: number;
  totalRevenue: number;
  activeBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalPayments: number;
  totalPayouts: number;
}

export interface UserGrowthData {
  date: string;
  count: number;
  cumulative: number;
}

export interface BookingTrendData {
  date: string;
  bookings: number;
  completed: number;
  cancelled: number;
  revenue: number;
}

export interface ServiceTypePopularity {
  serviceType: string;
  count: number;
  revenue: number;
}

export interface GeographicDistribution {
  state: string;
  city: string;
  userCount: number;
  providerCount: number;
  bookingCount: number;
}

class AnalyticsService {
  private api = axios.create({
    baseURL: adminApiUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.api.interceptors.request.use((config) => {
      const token = adminAuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 || error.response?.status === 403) {
          adminAuthService.logout();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async getPlatformStats(): Promise<PlatformStats> {
    const response = await this.api.get<{ data: { stats: PlatformStats } }>('/admin/analytics/platform-stats');
    return response.data.data.stats;
  }

  async getUserGrowthTrends(days: number = 30): Promise<UserGrowthData[]> {
    const response = await this.api.get<{ data: { trends: UserGrowthData[] } }>(
      `/admin/analytics/user-growth?days=${days}`
    );
    return response.data.data.trends;
  }

  async getBookingTrends(days: number = 30, period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<BookingTrendData[]> {
    const response = await this.api.get<{ data: { trends: BookingTrendData[] } }>(
      `/admin/analytics/booking-trends?days=${days}&period=${period}`
    );
    return response.data.data.trends;
  }

  async getServiceTypePopularity(): Promise<ServiceTypePopularity[]> {
    const response = await this.api.get<{ data: { popularity: ServiceTypePopularity[] } }>(
      '/admin/analytics/service-type-popularity'
    );
    return response.data.data.popularity;
  }

  async getGeographicDistribution(): Promise<GeographicDistribution[]> {
    const response = await this.api.get<{ data: { distribution: GeographicDistribution[] } }>(
      '/admin/analytics/geographic-distribution'
    );
    return response.data.data.distribution;
  }
}

export const analyticsService = new AnalyticsService();
